import {
  DefaultError,
  MutationFunctionContext,
  MutationKey,
  QueryFilters,
  useMutation as tanstackUseMutation,
  UseMutationOptions,
} from '@tanstack/react-query'
import { DocumentNode } from 'graphql'

import { request } from './client'
import { GraphQlResponse, handleResponse200, ResponseDataShape } from './response'
import { getKey } from './utils'

type Props<Variables extends Record<string, unknown>, ResponseData extends ResponseDataShape> = {
  mutation: DocumentNode
  onCompleted?: (data: ResponseData, variables: Variables) => Promise<void>
  options?: Omit<
    UseMutationOptions<GraphQlResponse<ResponseData> | void, DefaultError, Variables>,
    'mutationFn'
  >
  queriesToInvalidate?: QueryFilters['queryKey']
}

export const useMutation = <
  Variables extends Record<string, unknown>,
  ResponseData extends ResponseDataShape,
>({
  mutation,
  onCompleted,
  options,
  queriesToInvalidate,
}: Props<Variables, ResponseData>) => {
  type TData = GraphQlResponse<ResponseData> | void
  const key: MutationKey = getKey(mutation)

  return tanstackUseMutation<TData, DefaultError, Variables>({
    mutationFn: async (variables: Variables): Promise<TData> => {
      return request<Variables, ResponseData>({
        key,
        query: mutation,
        variables,
      })
    },
    mutationKey: key,
    onSuccess: (
      response: TData,
      variables: Variables,
      _onMutateResult: unknown,
      context: MutationFunctionContext,
    ): void => {
      const data: ResponseData | undefined = handleResponse200<ResponseData>({
        response,
      })

      if (data !== undefined) {
        if (onCompleted) {
          onCompleted(data, variables).finally(async (): Promise<void> => {
            await invalidateQueries(queriesToInvalidate, context)
          })
        } else {
          invalidateQueries(queriesToInvalidate, context)
        }
      } else {
        return undefined
      }
    },
    ...options,
  })
}

const invalidateQueries = async (
  queriesToInvalidate: QueryFilters['queryKey'],
  context: MutationFunctionContext,
): Promise<void> => {
  if (queriesToInvalidate === undefined || queriesToInvalidate.length === 0) {
    return
  }

  if (queriesToInvalidate.length === 1) {
    await context.client.invalidateQueries({
      queryKey: queriesToInvalidate,
    })
  } else {
    await Promise.all(
      queriesToInvalidate.map(async (queryKey: unknown): Promise<void> => {
        await context.client.invalidateQueries({
          queryKey: [queryKey],
        })
      }),
    )
  }
}
